<template>
  <div class="px-4 sm:px-6 lg:px-8 bg-gray-800 min-h-screen pt-10">
    <div class="sm:flex sm:items-center sm:justify-between">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-white">Artykuły</h1>
        <p class="mt-2 text-sm text-gray-400">
          Lista artykułów z szybkim podglądem i edycją po wyborze.
        </p>
      </div>
      <div class="mt-4 sm:mt-0 flex items-center gap-3">
        <button
          @click="createNewArticle"
          class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          Nowy artykuł
        </button>
        <button
          @click="fetchArticles"
          class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          :disabled="isFetching"
        >
          {{ isFetching ? 'Pobieranie...' : 'Odśwież listę' }}
        </button>
        <button
          @click="migrateLegacyArticles"
          class="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isMigratingLegacy || isFetching"
        >
          {{ isMigratingLegacy ? 'Migracja...' : 'Migruj legacy' }}
        </button>
      </div>
    </div>

    <div class="mt-6 rounded-xl border border-gray-700 bg-gray-900 overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-700">
        <p class="text-sm text-gray-300">
          Liczba artykułów: <span class="font-semibold text-white">{{ articleRows.length }}</span>
        </p>
      </div>

      <div v-if="articleRows.length" class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-700">
          <thead class="bg-gray-800/80">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-300">Tytuł</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-300">Slug</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-300">Publikacja</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-300">Aktualizacja</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-300">Zdjęcia</th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-300">Akcje</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800">
            <tr
              v-for="row in articleRows"
              :key="row.slug"
              :class="selectedArticleSlug === row.slug ? 'bg-gray-700/40' : 'bg-gray-900'"
            >
              <td class="px-4 py-3 text-sm text-white max-w-[360px] truncate" :title="row.title || '-'">
                {{ row.title || '-' }}
              </td>
              <td class="px-4 py-3 text-sm text-gray-300 font-mono">{{ row.slug }}</td>
              <td class="px-4 py-3 text-sm text-gray-300">{{ formatTableDate(row.publishDate) }}</td>
              <td class="px-4 py-3 text-sm text-gray-300">{{ formatTableDate(row.updatedAt) }}</td>
              <td class="px-4 py-3 text-sm text-gray-300">
                {{ hasFeaturedImage(row) ? '1 +' : '0 +' }} {{ Array.isArray(row.gallery) ? row.gallery.length : 0 }}
              </td>
              <td class="px-4 py-3">
                <div class="flex justify-end items-center gap-2">
                  <button
                    type="button"
                    @click="startEditingArticle(row)"
                    class="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Edytuj
                  </button>
                  <button
                    type="button"
                    @click="deleteArticle(row)"
                    :disabled="isDeletingSlug === row.slug"
                    class="px-3 py-1.5 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ isDeletingSlug === row.slug ? 'Usuwanie...' : 'Usuń' }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="px-4 py-10 text-center text-sm text-gray-400">
        Brak artykułów w bazie.
      </div>
    </div>

    <div class="mt-4 space-y-2">
      <p v-if="error" class="text-sm text-red-300">{{ error }}</p>
      <p v-if="successMessage" class="text-sm text-green-300">{{ successMessage }}</p>
    </div>

    <div v-if="isEditorOpen" class="mt-8">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold text-white">
          {{ selectedArticleSlug === NEW_ARTICLE_KEY ? 'Nowy artykuł' : `Edycja: ${selectedArticleSlug}` }}
        </h2>
        <button
          type="button"
          @click="closeEditor"
          class="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
        >
          Zamknij edytor
        </button>
      </div>

      <form @submit.prevent="generateJson" class="space-y-6">
        <div class="space-y-4">
          <h3 class="text-lg font-medium text-white">Podstawowe informacje</h3>
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label class="block text-gray-200 mb-1">Slug</label>
              <input v-model="articleData.slug" type="text" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200">
            </div>
            <div>
              <label class="block text-gray-200 mb-1">Meta Title</label>
              <input v-model="articleData.metaTitle" type="text" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200">
            </div>
            <div>
              <label class="block text-gray-200 mb-1">Meta Description</label>
              <textarea v-model="articleData.metaDescription" rows="2" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200"></textarea>
            </div>
            <div>
              <label class="block text-gray-200 mb-1">Data publikacji</label>
              <input v-model="articleData.publishDate" type="datetime-local" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200">
            </div>
          </div>
        </div>

        <div class="border border-gray-700 bg-gray-900 pt-6 p-6 rounded-xl">
          <h3 class="text-lg font-medium text-white">Autor</h3>
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 mt-4">
            <div>
              <label class="block text-gray-200 mb-1">Nazwa autora</label>
              <input v-model="articleData.author.name" type="text" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200">
            </div>
            <div>
              <label class="block text-gray-200 mb-1">Avatar autora</label>
              <input v-model="articleData.author.avatar" type="text" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200">
            </div>
          </div>
        </div>

        <div class="border-t border-gray-700 pt-6">
          <h3 class="text-lg font-medium text-white">Treść artykułu</h3>
          <div class="space-y-4 mt-4">
            <div>
              <label class="block text-gray-200 mb-1">Tytuł</label>
              <input v-model="articleData.title" type="text" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200">
            </div>
            <div>
              <label class="block text-gray-200 mb-1">Zajawka</label>
              <textarea v-model="articleData.excerpt" rows="3" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200"></textarea>
            </div>
            <div>
              <label class="block text-gray-200 mb-1">Treść artykułu (HTML)</label>
              <textarea v-model="articleData.content" rows="10" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200"></textarea>
            </div>
            <div>
              <label class="block text-gray-200 mb-1">Tagi (oddzielone przecinkami)</label>
              <input v-model="tagsInput" type="text" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200" placeholder="tag1, tag2, tag3">
            </div>
            <div>
              <label class="block text-gray-200 mb-1">Kategorie (oddzielone przecinkami)</label>
              <input v-model="categoriesInput" type="text" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200" placeholder="kategoria1, kategoria2">
            </div>

            <div class="border border-gray-700 bg-gray-900 pt-6 p-6 rounded-xl">
              <label class="block text-white font-bold mb-1">Zdjęcie główne</label>
              <div class="mb-3 flex items-center gap-3">
                <button type="button" @click="triggerFeaturedImageInput" class="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" :disabled="isUploadingFeatured">
                  {{ isUploadingFeatured ? 'Wgrywanie...' : 'Wgraj zdjęcie główne' }}
                </button>
                <input id="featured-article-photo-input" class="hidden" type="file" accept="image/*" @change="onFeaturedImagePick">
              </div>
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label class="block text-gray-200 mb-1 text-sm">URL zdjęcia</label>
                  <input v-model="articleData.featuredImage.url" type="text" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200">
                </div>
                <div>
                  <label class="block text-gray-200 mb-1 text-sm">Opis zdjęcia (alt)</label>
                  <input v-model="articleData.featuredImage.alt" type="text" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200">
                </div>
              </div>
              <div v-if="articleData.featuredImage.url" class="mt-4">
                <img :src="toAbsoluteUploadUrl(articleData.featuredImage.url)" :alt="articleData.featuredImage.alt || articleData.title || 'Zdjęcie główne artykułu'" class="w-full max-w-md rounded-md border border-gray-700 bg-gray-800 object-cover">
              </div>
            </div>

            <div class="border border-gray-700 bg-gray-900 pt-6 p-6 rounded-xl">
              <label class="block text-white font-bold mb-1">Galeria zdjęć</label>
              <div class="mb-3 flex items-center gap-3">
                <input id="gallery-article-photo-input" class="hidden" type="file" accept="image/*" multiple @change="onGalleryImagesPick">
                <button type="button" @click="triggerGalleryImagesInput" class="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" :disabled="isUploadingGallery">
                  {{ isUploadingGallery ? 'Wgrywanie...' : 'Dodaj zdjęcia z plików' }}
                </button>
              </div>
              <div class="space-y-4">
                <div v-for="(image, index) in galleryImages" :key="index" class="flex gap-4">
                  <div class="flex-1">
                    <label class="block text-gray-200 mb-1 text-sm">URL zdjęcia</label>
                    <input v-model="image.url" type="text" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200">
                  </div>
                  <div class="flex-1">
                    <label class="block text-gray-200 mb-1 text-sm">Opis zdjęcia (alt)</label>
                    <input v-model="image.alt" type="text" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200">
                  </div>
                  <div class="flex items-end">
                    <button type="button" @click="removeGalleryImage(index)" class="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">Usuń</button>
                  </div>
                </div>
                <div v-if="galleryImages.some((image) => image?.url)" class="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <div v-for="(image, index) in galleryImages.filter((item) => item?.url)" :key="`gallery-preview-${index}`" class="overflow-hidden rounded-md border border-gray-700 bg-gray-800">
                    <img :src="toAbsoluteUploadUrl(image.url)" :alt="image.alt || `Zdjęcie galerii ${index + 1}`" class="aspect-square w-full object-cover">
                  </div>
                </div>
                <button type="button" @click="addGalleryImage" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  Dodaj kolejne zdjęcie
                </button>
              </div>
            </div>

            <div>
              <label class="block text-gray-200 mb-1 mt-10">ID powiązanych artykułów (oddzielone przecinkami)</label>
              <input v-model="relatedArticlesInput" type="text" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200" placeholder="001, 002, 003">
            </div>
            <div>
              <label class="block text-gray-200 mb-1">Restauracje (JSON)</label>
              <textarea v-model="restaurantsInput" rows="10" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200" placeholder='[{"id": "rst-156", "name": "Nazwa restauracji", ...}]'></textarea>
            </div>
          </div>
        </div>

        <div class="flex space-x-4">
          <button type="submit" class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            Generuj JSON
          </button>
          <button type="button" @click="saveContent" class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700" :disabled="isSaving">
            {{ isSaving ? 'Zapisywanie...' : 'Zapisz' }}
          </button>
        </div>
      </form>

      <div class="mt-8">
        <label class="block text-gray-200 mb-1">Podgląd JSON</label>
        <textarea v-model="content" rows="20" class="w-full rounded-md border border-gray-600 p-2 bg-gray-700 text-gray-200" readonly></textarea>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { apiFetch } from '../composables/apiClient';
import { apiUrl } from '../composables/apiBase';

const NEW_ARTICLE_KEY = '__new__';

const emptyArticle = () => ({
  title: '',
  slug: '',
  metaTitle: '',
  metaDescription: '',
  publishDate: '',
  author: {
    name: '',
    avatar: '',
  },
  content: '',
  excerpt: '',
  tags: [],
  categories: [],
  relatedArticles: [],
  featuredImage: {
    url: '',
    alt: '',
  },
  restaurants: [],
});

const articleRows = ref([]);
const selectedArticleSlug = ref('');
const isDeletingSlug = ref('');
const content = ref('');
const error = ref('');
const successMessage = ref('');
const isSaving = ref(false);
const isFetching = ref(false);
const isUploadingFeatured = ref(false);
const isUploadingGallery = ref(false);
const isMigratingLegacy = ref(false);

const articleData = ref(emptyArticle());
const tagsInput = ref('');
const categoriesInput = ref('');
const relatedArticlesInput = ref('');
const restaurantsInput = ref('[]');
const galleryImages = ref([{ url: '', alt: '' }]);

const isEditorOpen = computed(() => Boolean(selectedArticleSlug.value));

const splitCommaValues = (value) =>
  String(value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

const slugify = (value) =>
  String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const toAbsoluteUploadUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return apiUrl(trimmed.startsWith('/') ? trimmed : `/${trimmed}`);
};

const toInputDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

const formatTableDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const hasFeaturedImage = (row) => {
  const featured = row?.featuredImage;
  if (typeof featured === 'string') {
    return featured.trim().length > 0;
  }
  if (featured && typeof featured === 'object') {
    return String(featured.url || '').trim().length > 0;
  }
  return false;
};

const normalizeGalleryForEditor = (value) => {
  if (!Array.isArray(value)) return [{ url: '', alt: '' }];
  const list = value
    .map((entry) => {
      if (typeof entry === 'string') {
        return { url: entry.trim(), alt: '' };
      }
      if (entry && typeof entry === 'object') {
        return {
          url: String(entry.url || entry.src || '').trim(),
          alt: String(entry.alt || '').trim(),
        };
      }
      return { url: '', alt: '' };
    })
    .filter((entry) => entry.url);
  return list.length ? list : [{ url: '', alt: '' }];
};

const clearFeedback = () => {
  error.value = '';
  successMessage.value = '';
};

const resetEditorForm = () => {
  articleData.value = emptyArticle();
  tagsInput.value = '';
  categoriesInput.value = '';
  relatedArticlesInput.value = '';
  restaurantsInput.value = '[]';
  galleryImages.value = [{ url: '', alt: '' }];
  content.value = '';
};

const createNewArticle = () => {
  clearFeedback();
  resetEditorForm();
  selectedArticleSlug.value = NEW_ARTICLE_KEY;
};

const closeEditor = () => {
  selectedArticleSlug.value = '';
  clearFeedback();
};

const applyArticleToEditor = (row) => {
  const featured =
    typeof row?.featuredImage === 'string'
      ? { url: row.featuredImage, alt: row.featuredImageAlt || '' }
      : {
          url: String(row?.featuredImage?.url || '').trim(),
          alt: String(row?.featuredImage?.alt || row?.featuredImageAlt || '').trim(),
        };

  articleData.value = {
    title: String(row?.title || ''),
    slug: String(row?.slug || ''),
    metaTitle: String(row?.metaTitle || ''),
    metaDescription: String(row?.metaDescription || ''),
    publishDate: toInputDateTime(row?.publishDate),
    author: {
      name: String(row?.author?.name || ''),
      avatar: String(row?.author?.avatar || ''),
    },
    content: String(row?.content || ''),
    excerpt: String(row?.excerpt || ''),
    tags: Array.isArray(row?.tags) ? row.tags : [],
    categories: Array.isArray(row?.categories) ? row.categories : [],
    relatedArticles: Array.isArray(row?.relatedArticles) ? row.relatedArticles : [],
    featuredImage: featured,
    restaurants: Array.isArray(row?.restaurants) ? row.restaurants : [],
  };

  tagsInput.value = articleData.value.tags.join(', ');
  categoriesInput.value = articleData.value.categories.join(', ');
  relatedArticlesInput.value = articleData.value.relatedArticles.join(', ');
  restaurantsInput.value = JSON.stringify(articleData.value.restaurants, null, 2);
  galleryImages.value = normalizeGalleryForEditor(row?.gallery);
  content.value = JSON.stringify(row || {}, null, 2);
};

const startEditingArticle = (row) => {
  clearFeedback();
  selectedArticleSlug.value = row.slug;
  applyArticleToEditor(row);
};

const resolveArticleSlug = () => {
  const slug = articleData.value.slug?.trim() || slugify(articleData.value.title);
  if (!slug) {
    throw new Error('Uzupełnij slug lub tytuł artykułu przed zapisem/uploadem.');
  }
  articleData.value.slug = slug;
  return slug;
};

const buildArticlePayload = () => {
  const slug = resolveArticleSlug();
  let restaurants = [];
  try {
    restaurants = JSON.parse(restaurantsInput.value || '[]');
  } catch (e) {
    throw new Error('Błąd w formacie JSON dla restauracji');
  }

  return {
    ...articleData.value,
    slug,
    tags: splitCommaValues(tagsInput.value),
    categories: splitCommaValues(categoriesInput.value),
    relatedArticles: splitCommaValues(relatedArticlesInput.value),
    gallery: galleryImages.value
      .map((img) => ({
        url: String(img.url ?? '').trim(),
        alt: String(img.alt ?? '').trim(),
      }))
      .filter((img) => img.url),
    restaurants,
    featuredImage: {
      url: String(articleData.value.featuredImage?.url ?? '').trim(),
      alt: String(articleData.value.featuredImage?.alt ?? '').trim(),
    },
  };
};

const addGalleryImage = () => {
  galleryImages.value.push({ url: '', alt: '' });
};

const removeGalleryImage = (index) => {
  galleryImages.value.splice(index, 1);
  if (!galleryImages.value.length) {
    galleryImages.value = [{ url: '', alt: '' }];
  }
};

const generateJson = () => {
  clearFeedback();
  try {
    const payload = buildArticlePayload();
    content.value = JSON.stringify(payload, null, 2);
  } catch (e) {
    error.value = e.message || 'Nie udało się wygenerować JSON.';
  }
};

const fetchArticles = async () => {
  isFetching.value = true;
  clearFeedback();
  try {
    const response = await apiFetch('/api/articles/blog');
    const responsePayload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(responsePayload?.error || responsePayload?.message || `Błąd pobierania (${response.status})`);
    }

    const rows = Array.isArray(responsePayload?.data) ? responsePayload.data : [];
    articleRows.value = rows;
    successMessage.value = `Pobrano ${rows.length} artykułów z bazy PostgreSQL.`;
  } catch (e) {
    error.value = `Błąd podczas pobierania artykułów: ${e.message || 'nieznany błąd'}`;
  } finally {
    isFetching.value = false;
  }
};

const migrateLegacyArticles = async () => {
  const confirmed = window.confirm(
    'Uruchomić migrację starych artykułów do nowej tabeli articles_blog? To może potrwać chwilę.'
  );
  if (!confirmed) return;

  isMigratingLegacy.value = true;
  clearFeedback();
  try {
    const response = await apiFetch('/api/articles/blog/migrate-legacy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        includeLegacyTable: true,
        overwriteExisting: false,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.error || payload?.message || `Błąd migracji (${response.status})`);
    }

    const result = payload?.data && typeof payload.data === 'object' ? payload.data : {};
    successMessage.value =
      `Migracja zakończona. Dodano: ${result.inserted || 0}, ` +
      `zaktualizowano: ${result.updated || 0}, ` +
      `pominięto (istniejące): ${result.skippedExisting || 0}, ` +
      `błędne: ${result.skippedInvalid || 0}.`;

    await fetchArticles();
  } catch (e) {
    error.value = e.message || 'Nie udało się uruchomić migracji legacy.';
  } finally {
    isMigratingLegacy.value = false;
  }
};

const saveContent = async () => {
  clearFeedback();
  isSaving.value = true;
  try {
    const payload = buildArticlePayload();
    content.value = JSON.stringify(payload, null, 2);
    const response = await apiFetch('/api/articles/blog', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const responsePayload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(responsePayload?.error || responsePayload?.message || `Błąd zapisu (${response.status})`);
    }

    successMessage.value = `Pomyślnie zapisano artykuł (${payload.slug})`;
    selectedArticleSlug.value = payload.slug;
    await fetchArticles();

    const saved = responsePayload?.data;
    if (saved && typeof saved === 'object') {
      applyArticleToEditor(saved);
    }
  } catch (e) {
    error.value = e.message || 'Nie udało się zapisać artykułu.';
  } finally {
    isSaving.value = false;
  }
};

const deleteArticle = async (row) => {
  if (!row?.slug) return;
  const confirmed = window.confirm(`Czy na pewno usunąć artykuł "${row.title || row.slug}"?`);
  if (!confirmed) return;

  clearFeedback();
  isDeletingSlug.value = row.slug;
  try {
    const response = await apiFetch(`/api/articles/blog/${encodeURIComponent(row.slug)}`, {
      method: 'DELETE',
    });
    const responsePayload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(responsePayload?.error || responsePayload?.message || `Błąd usuwania (${response.status})`);
    }

    successMessage.value = `Usunięto artykuł (${row.slug}).`;
    if (selectedArticleSlug.value === row.slug) {
      closeEditor();
    }
    await fetchArticles();
  } catch (e) {
    error.value = e.message || 'Nie udało się usunąć artykułu.';
  } finally {
    isDeletingSlug.value = '';
  }
};

const uploadSingleArticleImage = async (file) => {
  const slug = resolveArticleSlug();
  const form = new FormData();
  form.append('photo', file);
  const response = await apiFetch(`/api/articles/blog/${encodeURIComponent(slug)}/photo`, {
    method: 'POST',
    body: form,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || payload?.message || `Upload failed (${response.status})`);
  }
  return payload?.path || payload?.url || '';
};

const triggerFeaturedImageInput = () => {
  const input = document.getElementById('featured-article-photo-input');
  if (input) input.click();
};

const triggerGalleryImagesInput = () => {
  const input = document.getElementById('gallery-article-photo-input');
  if (input) input.click();
};

const onFeaturedImagePick = async (event) => {
  const files = Array.from(event?.target?.files || []);
  if (!files.length) return;
  clearFeedback();
  isUploadingFeatured.value = true;
  try {
    const uploadedPath = await uploadSingleArticleImage(files[0]);
    if (uploadedPath) {
      articleData.value.featuredImage.url = uploadedPath;
      if (!articleData.value.featuredImage.alt && articleData.value.title) {
        articleData.value.featuredImage.alt = articleData.value.title;
      }
      successMessage.value = 'Zdjęcie główne zostało wgrane.';
    }
  } catch (e) {
    error.value = e.message || 'Błąd uploadu zdjęcia głównego.';
  } finally {
    isUploadingFeatured.value = false;
    if (event?.target) event.target.value = '';
  }
};

const onGalleryImagesPick = async (event) => {
  const files = Array.from(event?.target?.files || []);
  if (!files.length) return;
  clearFeedback();
  isUploadingGallery.value = true;
  const uploaded = [];
  const failed = [];

  try {
    for (const file of files) {
      try {
        const uploadedPath = await uploadSingleArticleImage(file);
        if (uploadedPath) {
          uploaded.push({
            url: uploadedPath,
            alt: articleData.value.title || '',
          });
        }
      } catch (singleError) {
        failed.push(`${file.name}: ${singleError.message}`);
      }
    }

    if (uploaded.length) {
      galleryImages.value.push(...uploaded);
      successMessage.value = `Wgrano ${uploaded.length} zdjęć do galerii.`;
    }
    if (failed.length) {
      throw new Error(`Nie udało się wgrać ${failed.length} plików:\n${failed.join('\n')}`);
    }
  } catch (e) {
    error.value = e.message || 'Błąd uploadu galerii.';
  } finally {
    isUploadingGallery.value = false;
    if (event?.target) event.target.value = '';
  }
};

onMounted(() => {
  fetchArticles();
});
</script>